import { useQuery } from 'react-query'
import { useAuth } from '../context/AuthContext'
import axios from 'axios'

/**
 * Hook to get current user's permissions
 * @returns {Object} - Permissions data and utilities
 */
export const usePermissions = () => {
  const { user } = useAuth()
  
  // Only fetch permissions for client users
  const { data: permissionData, isLoading, error } = useQuery(
    ['user-permissions', user?.id],
    async () => {
      if (!user || user.role !== 'client') {
        return { permissions: {}, clientId: null }
      }
      
      // Get client info first
      const clientResponse = await axios.get('/clients/profile')
      const clientId = clientResponse.data.id
      
      // Get permissions
      const permissionsResponse = await axios.get(`/permissions/client/${clientId}`)
      
      return {
        permissions: permissionsResponse.data.permissions,
        clientId: clientId,
        metadata: permissionsResponse.data.metadata
      }
    },
    {
      enabled: !!user,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false
    }
  )

  // For admin users, return all permissions as true
  if (user?.role === 'admin') {
    return {
      permissions: {
        can_edit_text: true,
        can_upload_media: true,
        can_create_pages: true,
        can_create_sites: true,
        can_view_analytics: true,
        can_manage_domains: true,
        can_access_support: true,
        can_view_billing: true,
        can_publish_sites: true,
        can_delete_sites: true,
        can_export_data: true
      },
      hasPermission: () => true,
      hasAnyPermission: () => true,
      hasAllPermissions: () => true,
      isLoading: false,
      error: null,
      clientId: null
    }
  }

  const permissions = permissionData?.permissions || {}

  // Helper functions
  const hasPermission = (permission) => {
    return Boolean(permissions[permission])
  }

  const hasAnyPermission = (permissionsList) => {
    return permissionsList.some(permission => hasPermission(permission))
  }

  const hasAllPermissions = (permissionsList) => {
    return permissionsList.every(permission => hasPermission(permission))
  }

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    error,
    clientId: permissionData?.clientId,
    metadata: permissionData?.metadata
  }
}

/**
 * Hook to check specific permission
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has permission
 */
export const useHasPermission = (permission) => {
  const { hasPermission, isLoading } = usePermissions()
  
  if (isLoading) return true // Allow access while loading
  return hasPermission(permission)
}

/**
 * Hook to check multiple permissions (any)
 * @param {Array<string>} permissionsList - List of permissions to check
 * @returns {boolean} - Whether user has any of the permissions
 */
export const useHasAnyPermission = (permissionsList) => {
  const { hasAnyPermission, isLoading } = usePermissions()
  
  if (isLoading) return true // Allow access while loading
  return hasAnyPermission(permissionsList)
}

/**
 * Hook to check multiple permissions (all)
 * @param {Array<string>} permissionsList - List of permissions to check
 * @returns {boolean} - Whether user has all of the permissions
 */
export const useHasAllPermissions = (permissionsList) => {
  const { hasAllPermissions, isLoading } = usePermissions()
  
  if (isLoading) return true // Allow access while loading
  return hasAllPermissions(permissionsList)
}